import { normalizeLanguage } from '../locales/index.js';

const PUBLIC_CREDIT_ONLY_COPY = {
  en: {
    label: 'Credit plans',
    body: 'Stocktag uses managed credits. Choose a monthly credit plan or add non-expiring one-time credits to continue.'
  },
  tr: {
    label: 'Kredi planları',
    body: 'Stocktag yönetilen kredilerle çalışır. Devam etmek için aylık kredi planı seçin veya süresiz ek kredi alın.'
  },
  es: {
    label: 'Planes de créditos',
    body: 'Stocktag usa créditos gestionados. Elige un plan mensual o añade créditos de pago único que no caducan.'
  },
  fr: {
    label: 'Offres de crédits',
    body: 'Stocktag utilise des crédits gérés. Choisissez une offre mensuelle ou ajoutez des crédits ponctuels sans expiration.'
  },
  de: {
    label: 'Credit-Tarife',
    body: 'Stocktag verwendet verwaltete Credits. Wähle einen Monatstarif oder füge einmalige Credits ohne Ablaufdatum hinzu.'
  },
  it: {
    label: 'Piani di crediti',
    body: 'Stocktag usa crediti gestiti. Scegli un piano mensile o aggiungi crediti una tantum senza scadenza.'
  },
  id: {
    label: 'Paket kredit',
    body: 'Stocktag menggunakan kredit terkelola. Pilih paket bulanan atau tambahkan kredit sekali beli yang tidak kedaluwarsa.'
  },
  th: {
    label: 'แพ็กเกจเครดิต',
    body: 'Stocktag ใช้เครดิตที่จัดการให้ เลือกแพ็กเกจรายเดือนหรือเติมเครดิตแบบซื้อครั้งเดียวที่ไม่มีวันหมดอายุ'
  },
  bn: {
    label: 'ক্রেডিট প্ল্যান',
    body: 'Stocktag পরিচালিত ক্রেডিট ব্যবহার করে। মাসিক প্ল্যান বেছে নিন বা মেয়াদহীন এককালীন ক্রেডিট যোগ করুন।'
  },
  hi: {
    label: 'क्रेडिट प्लान',
    body: 'Stocktag प्रबंधित क्रेडिट का उपयोग करता है। मासिक प्लान चुनें या बिना समाप्ति वाले एकमुश्त क्रेडिट जोड़ें।'
  },
  pt: {
    label: 'Planos de créditos',
    body: 'O Stocktag usa créditos geridos. Escolha um plano mensal ou adicione créditos únicos que não expiram.'
  }
};

// Public pages must describe only Stocktag-managed credit access. Keep this
// deliberately broader than the retired product name: copy such as "no API
// key needed" still advertises the existence of the private access path.
const PRIVATE_ACCESS_PATTERNS = [
  /\bbyok\b/i,
  /bring your own (?:api )?keys?\b/i,
  /(?:use|add|save|enter|connect|manage|provide|configure|set up|supply|remove|show|hide) (?:your |an? )?(?:own )?(?:provider )?(?:api )?keys?\b/i,
  /(?:your|their|my|our|own|provider|openai|gemini|anthropic|groq|xai) (?:api )?keys?\b/i,
  /api[ _-]?keys?\b/i,
  /(?:third[- ]party|external|selected) provider credentials?/i,
  /(?:provider|external) account (?:setup|required|needed)/i,
  /(?:no|without) (?:an? )?(?:api )?key\b(?: setup)?/i,
  /keys?\b (?:needed|required|setup|connected|saved|removed)/i,
  /api anahtar(?:ı|ları|ınız|ınızı|larını|ını)?/iu,
  /kendi api anahtar(?:ı|ınız|ınızı|larını)?/iu,
  /claves? (?:de )?api/iu,
  /(?:tu|su|propia|propio) claves? (?:de )?api/iu,
  /cl[ée]s? api/iu,
  /(?:votre|sa|propre) cl[ée]s? api/iu,
  /api[- ]schl[üu]ssel/iu,
  /(?:eigene[nrsm]?|deine[nrsm]?) api[- ]schl[üu]ssel/iu,
  /chiav[ei] api/iu,
  /(?:tua|propria) chiave api/iu,
  /chaves? (?:de )?api/iu,
  /(?:sua|pr[óo]pria) chave (?:de )?api/iu,
  /kunci api/iu,
  /kunci api (?:anda|sendiri)/iu,
  /คีย์\s*api/iu,
  /api\s*কী/iu,
  /নিজের\s*(?:api\s*)?কী/iu,
  /api\s*कुंज(?:ी|ियाँ)/iu,
  /अपनी\s*(?:api\s*)?कुंज(?:ी|ियाँ)/iu
];

const RETIRED_PUBLIC_BYOK_PATTERN = new RegExp(
  PRIVATE_ACCESS_PATTERNS.map((pattern) => `(?:${pattern.source})`).join('|'),
  'iu'
);
const RETIRED_PUBLIC_BYOK_KEY = /(?:^|_)(?:byok|unlimited_byok|own_key|api_keys?|provider_keys?)(?:_|$)|apiKey|ApiKey|^credit_free_limit_|^pricing_(?:simple_(?:title|body)|compare_title)$/i;
const SHORT_COPY_KEY = /(?:^|_)(?:title|heading|label|badge|cta|action|tab|name)(?:_|$)/i;

export const getPublicCreditOnlyCopy = (language = 'en') => (
  PUBLIC_CREDIT_ONLY_COPY[normalizeLanguage(language)] || PUBLIC_CREDIT_ONLY_COPY.en
);

export const containsRetiredPublicByokCopy = (value) => (
  RETIRED_PUBLIC_BYOK_PATTERN.test(JSON.stringify(value || ''))
);

export const isPrivateAccessLocaleKey = (key = '') => RETIRED_PUBLIC_BYOK_KEY.test(String(key));

const sanitizePublicLegalNode = (value, language = 'en', key = '') => {
  if (typeof value === 'string') {
    return RETIRED_PUBLIC_BYOK_KEY.test(key) || RETIRED_PUBLIC_BYOK_PATTERN.test(value)
      ? null
      : value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizePublicLegalNode(item, language, key))
      .filter((item) => item !== null && item !== undefined);
  }

  if (!value || typeof value !== 'object') return value;

  if (
    typeof value.heading === 'string'
    && RETIRED_PUBLIC_BYOK_PATTERN.test(value.heading)
  ) {
    return null;
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([childKey, childValue]) => [
        childKey,
        sanitizePublicLegalNode(childValue, language, childKey)
      ])
      .filter(([, childValue]) => childValue !== null && childValue !== undefined)
  );
};

// Legal content is structurally different from marketing copy: a private-access
// sentence must not remove the entire surrounding section. Preserve every
// unaffected paragraph/list item and drop only the retired private-access node.
export const sanitizePublicCreditOnlyLegalContent = (value, language = 'en') => (
  sanitizePublicLegalNode(value, language) || {}
);

export const sanitizePublicCreditOnlyContent = (value, language = 'en', key = '') => {
  const replacement = getPublicCreditOnlyCopy(language);

  if (typeof value === 'string') {
    if (!RETIRED_PUBLIC_BYOK_KEY.test(key) && !RETIRED_PUBLIC_BYOK_PATTERN.test(value)) return value;
    return SHORT_COPY_KEY.test(key) ? replacement.label : replacement.body;
  }

  if (Array.isArray(value)) {
    return value
      .filter((item) => !containsRetiredPublicByokCopy(item))
      .map((item) => sanitizePublicCreditOnlyContent(item, language, key));
  }

  if (!value || typeof value !== 'object') return value;

  return Object.fromEntries(
    Object.entries(value)
      .filter(([childKey]) => !isPrivateAccessLocaleKey(childKey))
      .map(([childKey, childValue]) => [
        childKey,
        sanitizePublicCreditOnlyContent(childValue, language, childKey)
      ])
  );
};

// Authenticated credit customers use the full Studio locale shape, so keep
// every key available to components while replacing any retired private-access
// wording. This prevents stale UI branches from falling back to BYOK copy.
export const sanitizeCreditOnlyMemberContent = (value, language = 'en', key = '') => {
  const replacement = getPublicCreditOnlyCopy(language);

  if (typeof value === 'string') {
    if (!RETIRED_PUBLIC_BYOK_KEY.test(key) && !RETIRED_PUBLIC_BYOK_PATTERN.test(value)) return value;
    return SHORT_COPY_KEY.test(key) ? replacement.label : replacement.body;
  }

  if (Array.isArray(value)) {
    return value
      .filter((item) => !containsRetiredPublicByokCopy(item))
      .map((item) => sanitizeCreditOnlyMemberContent(item, language, key));
  }

  if (!value || typeof value !== 'object') return value;

  return Object.fromEntries(
    Object.entries(value).map(([childKey, childValue]) => [
      childKey,
      sanitizeCreditOnlyMemberContent(childValue, language, childKey)
    ])
  );
};

export default sanitizePublicCreditOnlyContent;
